/**
 * RelationshipsPage — Cross-Account Relational Analysis (CARA)
 *
 * Lists existing relationships and provides a single-page builder
 * to create/edit/validate relationships between data source accounts.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  GitBranch,
  Plus,
  Trash2,
  Edit2,
  Play,
  X,
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { integrationsApi, handleApiError } from '../../services/api';
import {
  caraApi,
} from '../../services/cara-api';
import type {
  RelationshipRecord,
  RelationshipDefinition,
  FilterGroup,
  FilterCondition,
  SchemaData,
  FilterFieldOption,
} from '../../services/cara-api';

// ============================================================================
// Constants
// ============================================================================

const BOND_TYPES = [
  { value: 'direct_proportional', label: 'Direct Proportional', description: 'B = k * A' },
  { value: 'linear_with_offset', label: 'Linear with Offset', description: 'B = mA + c' },
  { value: 'ratio_bounded', label: 'Ratio Bounded', description: 'min ≤ B/A ≤ max' },
  { value: 'correlation', label: 'Correlation', description: 'Pearson r(A, B)' },
];

const DIRECTIONS = [
  { value: 'a_drives_b', label: 'A drives B' },
  { value: 'b_drives_a', label: 'B drives A' },
  { value: 'bidirectional', label: 'Bidirectional' },
];

const GRANULARITIES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];

const METRIC_FUNCTIONS = [
  { value: 'SUM', label: 'SUM' },
  { value: 'AVG', label: 'AVG' },
  { value: 'COUNT', label: 'COUNT' },
  { value: 'COUNT_DISTINCT', label: 'COUNT DISTINCT' },
  { value: 'MIN', label: 'MIN' },
  { value: 'MAX', label: 'MAX' },
  { value: 'MEDIAN', label: 'MEDIAN' },
  { value: 'STDDEV', label: 'STDDEV' },
];

const DEFAULT_TIME_ANCHORS = [
  { value: 'transactions.transaction_timestamp', label: 'Transaction Timestamp' },
  { value: 'transactions.created_at', label: 'Created At' },
];

const FILTER_OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'in', label: 'IN' },
  { value: 'not_in', label: 'NOT IN' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'like', label: 'LIKE' },
  { value: 'is_null', label: 'IS NULL' },
  { value: 'is_not_null', label: 'IS NOT NULL' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-muted text-foreground',
  active: 'bg-success/15 text-success',
  paused: 'bg-warn/15 text-warn',
  archived: 'bg-destructive/15 text-destructive',
};

// ============================================================================
// Helper: Empty filter condition
// ============================================================================

function emptyCondition(): FilterCondition {
  return { field: '', operator: 'eq', value: '' };
}

function emptyFilterGroup(): FilterGroup {
  return { logic: 'AND', conditions: [] };
}

// ============================================================================
// Sub-component: Filter Builder (recursive — supports AND/OR + nested groups)
// ============================================================================

/** Type guard: distinguishes nested FilterGroup from leaf FilterCondition */
function isFilterGroup(item: FilterCondition | FilterGroup): item is FilterGroup {
  return 'logic' in item && 'conditions' in item;
}

interface FilterBuilderProps {
  filters: FilterGroup;
  onChange: (filters: FilterGroup) => void;
  label: string;
  filterFields?: FilterFieldOption[];
  /** Depth counter — 0 = top-level group, >0 = nested sub-group */
  depth?: number;
  /** Called when the user removes this entire group (only for nested groups) */
  onRemove?: () => void;
}

/** Operators where a dropdown of known values makes sense */
const SELECTABLE_OPERATORS = ['eq', 'neq', 'in', 'not_in'];

/** Build field options from schema filter fields */
function buildFieldOptions(filterFields?: FilterFieldOption[]) {
  if (filterFields && filterFields.length > 0) {
    return filterFields.map((f) => ({
      value: `${f.mapping_level}.${f.slug}`,
      label: f.label,
    }));
  }
  return [{ value: '', label: 'No fields available' }];
}

/** Look up distinct values for a field from the schema filter fields */
function getDistinctValues(field: string, filterFields?: FilterFieldOption[]): string[] {
  if (!filterFields) return [];
  const match = filterFields.find(f => `${f.mapping_level}.${f.slug}` === field);
  return match?.distinct_values || [];
}

const FilterBuilder: React.FC<FilterBuilderProps> = ({
  filters,
  onChange,
  label,
  filterFields,
  depth = 0,
  onRemove,
}) => {
  const items = filters.conditions || [];
  const fieldOptions = buildFieldOptions(filterFields);

  // --- Mutators -----------------------------------------------------------

  const toggleLogic = () => {
    onChange({ ...filters, logic: filters.logic === 'AND' ? 'OR' : 'AND' });
  };

  const addCondition = () => {
    onChange({ ...filters, conditions: [...items, emptyCondition()] });
  };

  const addGroup = () => {
    onChange({
      ...filters,
      conditions: [...items, emptyFilterGroup()],
    });
  };

  const removeItem = (idx: number) => {
    onChange({ ...filters, conditions: items.filter((_, i) => i !== idx) });
  };

  const updateCondition = (
    idx: number,
    field: 'field' | 'operator' | 'value',
    value: string,
  ) => {
    const updated = [...items];
    const cond = { ...(updated[idx] as FilterCondition) };
    if (field === 'field') {
      cond.field = value;
      cond.value = '';
    } else if (field === 'operator') {
      cond.operator = value;
    } else {
      cond.value = value;
    }
    updated[idx] = cond;
    onChange({ ...filters, conditions: updated });
  };

  const updateNestedGroup = (idx: number, newGroup: FilterGroup) => {
    const updated = [...items];
    updated[idx] = newGroup;
    onChange({ ...filters, conditions: updated });
  };

  // --- Render -------------------------------------------------------------

  const isNested = depth > 0;

  return (
    <div
      className={
        isNested
          ? 'space-y-2 pl-3 border-l-2 border-info/30 ml-1'
          : 'space-y-2'
      }
    >
      {/* Header row */}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-sm font-medium text-foreground">{label}</label>

        {/* Logic toggle — show when there are 2+ items */}
        {items.length >= 2 && (
          <button
            type="button"
            onClick={toggleLogic}
            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              filters.logic === 'AND'
                ? 'bg-info/15 text-info'
                : 'bg-warn/15 text-warn'
            }`}
          >
            {filters.logic}
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={addCondition}
            className="text-xs text-info hover:text-info/80"
          >
            + Filter
          </button>
          <button
            type="button"
            onClick={addGroup}
            className="text-xs text-primary hover:text-primary/80"
          >
            + Group
          </button>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-destructive hover:text-destructive/80"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Items (conditions + nested groups) */}
      {items.map((item, idx) => {
        // --- Nested group ---
        if (isFilterGroup(item)) {
          return (
            <FilterBuilder
              key={`group-${idx}`}
              filters={item}
              onChange={(g) => updateNestedGroup(idx, g)}
              label={`Group`}
              filterFields={filterFields}
              depth={depth + 1}
              onRemove={() => removeItem(idx)}
            />
          );
        }

        // --- Leaf condition ---
        const cond = item as FilterCondition;
        const availableValues = getDistinctValues(cond.field, filterFields);
        const showDropdown =
          availableValues.length > 0 && SELECTABLE_OPERATORS.includes(cond.operator);

        return (
          <div key={`cond-${idx}`} className="flex items-center gap-2">
            {/* Logic badge between conditions */}
            {idx > 0 && !isFilterGroup(items[idx - 1]) && (
              <span className="text-[10px] font-medium text-muted-foreground w-6 text-center flex-shrink-0">
                {filters.logic}
              </span>
            )}
            <select
              value={cond.field}
              onChange={(e) => updateCondition(idx, 'field', e.target.value)}
              className="flex-1 text-sm rounded border border-input px-2 py-1 text-foreground bg-card"
            >
              {fieldOptions.map((fo) => (
                <option key={fo.value} value={fo.value}>
                  {fo.label}
                </option>
              ))}
            </select>
            <select
              value={cond.operator}
              onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
              className="w-24 text-sm rounded border border-input px-2 py-1 text-foreground bg-card"
            >
              {FILTER_OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
            {!['is_null', 'is_not_null'].includes(cond.operator) &&
              (showDropdown ? (
                <select
                  value={String(cond.value || '')}
                  onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                  className="flex-1 text-sm rounded border border-input px-2 py-1 text-foreground bg-card"
                >
                  <option value="">Select value...</option>
                  {availableValues.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={String(cond.value || '')}
                  onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                  placeholder="value"
                  className="flex-1 text-sm rounded border border-input px-2 py-1 text-foreground bg-card"
                />
              ))}
            <button
              type="button"
              onClick={() => removeItem(idx)}
              className="text-destructive hover:text-destructive/80"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Sub-component: Side Config
// ============================================================================

interface SideConfigProps {
  label: string;
  accountId: string;
  onAccountChange: (id: string) => void;
  accounts: Array<{ id: string; name: string; institutionName?: string }>;
  metricField: string;
  onMetricFieldChange: (v: string) => void;
  metricFunction: string;
  onMetricFunctionChange: (v: string) => void;
  timeAnchor: string;
  onTimeAnchorChange: (v: string) => void;
  granularity: string;
  onGranularityChange: (v: string) => void;
  filters: FilterGroup;
  onFiltersChange: (f: FilterGroup) => void;
  joinDetails: boolean;
  onJoinDetailsChange: (v: boolean) => void;
  detailFilters: FilterGroup;
  onDetailFiltersChange: (f: FilterGroup) => void;
  periodOffset?: number;
  onPeriodOffsetChange?: (v: number) => void;
  dataLag: number;
  onDataLagChange: (v: number) => void;
  schema?: SchemaData | null;
}

const SideConfig: React.FC<SideConfigProps> = ({
  label,
  accountId,
  onAccountChange,
  accounts,
  metricField,
  onMetricFieldChange,
  metricFunction,
  onMetricFunctionChange,
  timeAnchor,
  onTimeAnchorChange,
  granularity,
  onGranularityChange,
  filters,
  onFiltersChange,
  joinDetails,
  onJoinDetailsChange,
  detailFilters,
  onDetailFiltersChange,
  periodOffset,
  onPeriodOffsetChange,
  dataLag,
  onDataLagChange,
  schema,
}) => {
  const timeAnchors = schema?.time_anchors ?? DEFAULT_TIME_ANCHORS;

  const metricFieldOptions: Array<{ value: string; label: string; level: string }> = (schema?.metric_fields ?? []).map(f => ({
    value: `${f.mapping_level}.${f.slug}`,
    label: f.label,
    level: f.mapping_level,
  }));

  const txMetrics = metricFieldOptions.filter(f => f.level === 'transactions');
  const detailMetrics = metricFieldOptions.filter(f => f.level !== 'transactions');

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <h4 className="font-semibold text-foreground">{label}</h4>

      {/* Account selector */}
      <div>
        <label className="text-sm font-medium text-foreground">Account</label>
        <select
          value={accountId}
          onChange={(e) => onAccountChange(e.target.value)}
          className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
        >
          <option value="">Select account...</option>
          {accounts.map((acc) => (
            <option key={acc.id} value={acc.id}>
              {acc.name}{acc.institutionName ? ` (${acc.institutionName})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Metric */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-foreground">Metric Function</label>
          <select
            value={metricFunction}
            onChange={(e) => onMetricFunctionChange(e.target.value)}
            className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
          >
            {METRIC_FUNCTIONS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-foreground">Metric Field</label>
          <select
            value={metricField}
            onChange={(e) => onMetricFieldChange(e.target.value)}
            className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
          >
            {metricFieldOptions.length === 0 && (
              <option value="">Select an account first</option>
            )}
            {txMetrics.length > 0 && (
              <optgroup label="Transactions">
                {txMetrics.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </optgroup>
            )}
            {detailMetrics.length > 0 && (
              <optgroup label="Transaction Details">
                {detailMetrics.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      </div>

      {/* Aggregation */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-foreground">Time Anchor</label>
          <select
            value={timeAnchor}
            onChange={(e) => onTimeAnchorChange(e.target.value)}
            className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
          >
            {timeAnchors.map((ta) => (
              <option key={ta.value} value={ta.value}>{ta.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-foreground">Granularity</label>
          <select
            value={granularity}
            onChange={(e) => onGranularityChange(e.target.value)}
            className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
          >
            {GRANULARITIES.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Data Lag */}
      <div>
        <label className="text-sm font-medium text-foreground">Data Lag (days)</label>
        <input
          type="number"
          min="0"
          value={dataLag}
          onChange={(e) => onDataLagChange(parseInt(e.target.value) || 0)}
          className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Processing delay in days (e.g., QB data lags POS by 45 days).
        </p>
      </div>

      {/* Period Offset (Side B only) */}
      {onPeriodOffsetChange !== undefined && (
        <div>
          <label className="text-sm font-medium text-foreground">Period Offset</label>
          <input
            type="number"
            value={periodOffset || 0}
            onChange={(e) => onPeriodOffsetChange(parseInt(e.target.value) || 0)}
            className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
          />
          <p className="text-xs text-muted-foreground mt-1">
            0 = same period. +1 = Side B data appears 1 period after Side A.
          </p>
        </div>
      )}

      {/* Filters */}
      <FilterBuilder filters={filters} onChange={onFiltersChange} label="Filters" filterFields={schema?.filter_fields} />

      {/* Join details toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={joinDetails}
          onChange={(e) => onJoinDetailsChange(e.target.checked)}
          className="rounded border-input"
        />
        <label className="text-sm text-foreground">Join transaction_details</label>
      </div>

      {joinDetails && (
        <FilterBuilder filters={detailFilters} onChange={onDetailFiltersChange} label="Detail Filters" filterFields={schema?.filter_fields} />
      )}
    </div>
  );
};

// ============================================================================
// Main Page Component
// ============================================================================

const RelationshipsPage: React.FC = () => {
  const queryClient = useQueryClient();

  // List/builder toggle
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [validatingId, setValidatingId] = useState<string | null>(null);

  // Builder form state
  const [name, setName] = useState('');

  // Side A
  const [sideAAccountId, setSideAAccountId] = useState('');
  const [sideAMetricField, setSideAMetricField] = useState('');
  const [sideAMetricFunction, setSideAMetricFunction] = useState('SUM');
  const [sideATimeAnchor, setSideATimeAnchor] = useState('transactions.transaction_timestamp');
  const [sideAGranularity, setSideAGranularity] = useState('month');
  const [sideAFilters, setSideAFilters] = useState<FilterGroup>(emptyFilterGroup());
  const [sideAJoinDetails, setSideAJoinDetails] = useState(false);
  const [sideADetailFilters, setSideADetailFilters] = useState<FilterGroup>(emptyFilterGroup());

  const [sideADataLag, setSideADataLag] = useState(0);

  // Side B
  const [sideBAccountId, setSideBAccountId] = useState('');
  const [sideBMetricField, setSideBMetricField] = useState('');
  const [sideBMetricFunction, setSideBMetricFunction] = useState('SUM');
  const [sideBTimeAnchor, setSideBTimeAnchor] = useState('transactions.transaction_timestamp');
  const [sideBGranularity, setSideBGranularity] = useState('month');
  const [sideBFilters, setSideBFilters] = useState<FilterGroup>(emptyFilterGroup());
  const [sideBJoinDetails, setSideBJoinDetails] = useState(false);
  const [sideBDetailFilters, setSideBDetailFilters] = useState<FilterGroup>(emptyFilterGroup());
  const [sideBPeriodOffset, setSideBPeriodOffset] = useState(0);
  const [sideBDataLag, setSideBDataLag] = useState(0);

  // Bond
  const [bondType, setBondType] = useState('direct_proportional');
  const [bondDirection, setBondDirection] = useState('a_drives_b');
  const [bondParams, setBondParams] = useState<Record<string, number | undefined>>({});

  // Queries
  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: integrationsApi.getAccounts,
  });

  const { data: relationshipsData, isLoading: relationshipsLoading } = useQuery({
    queryKey: ['relationships'],
    queryFn: () => caraApi.listRelationships(),
  });

  // Fetch schema (distinct field values) when an account is selected
  const { data: sideASchemaData } = useQuery({
    queryKey: ['cara-schema', sideAAccountId],
    queryFn: () => caraApi.getSchema(sideAAccountId),
    enabled: !!sideAAccountId,
  });

  const { data: sideBSchemaData } = useQuery({
    queryKey: ['cara-schema', sideBAccountId],
    queryFn: () => caraApi.getSchema(sideBAccountId),
    enabled: !!sideBAccountId,
  });

  const sideASchema: SchemaData | null = sideASchemaData?.data || null;
  const sideBSchema: SchemaData | null = sideBSchemaData?.data || null;

  useEffect(() => {
    if (sideASchema?.metric_fields?.length && !sideAMetricField) {
      const first = sideASchema.metric_fields[0];
      setSideAMetricField(`${first.mapping_level}.${first.slug}`);
    }
  }, [sideASchema, sideAMetricField]);

  useEffect(() => {
    if (sideBSchema?.metric_fields?.length && !sideBMetricField) {
      const first = sideBSchema.metric_fields[0];
      setSideBMetricField(`${first.mapping_level}.${first.slug}`);
    }
  }, [sideBSchema, sideBMetricField]);

  const accounts = accountsData?.accounts ?? [];
  const relationships: RelationshipRecord[] = relationshipsData?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: caraApi.createRelationship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relationships'] });
      toast.success('Relationship created');
      resetBuilder();
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to create relationship');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; definition?: RelationshipDefinition; status?: string };
    }) => caraApi.updateRelationship(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relationships'] });
      toast.success('Relationship updated');
      resetBuilder();
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update relationship');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: caraApi.deleteRelationship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relationships'] });
      toast.success('Relationship deleted');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to delete relationship');
    },
  });

  const validateMutation = useMutation({
    mutationFn: caraApi.validateRelationship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relationships'] });
      toast.success('Validation complete');
      setValidatingId(null);
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Validation failed');
      setValidatingId(null);
    },
  });

  // ============================================================================
  // Helpers
  // ============================================================================

  const resetBuilder = () => {
    setShowBuilder(false);
    setEditingId(null);
    setName('');
    setSideAAccountId('');
    setSideAMetricField('');
    setSideAMetricFunction('SUM');
    setSideATimeAnchor('transactions.transaction_timestamp');
    setSideAGranularity('month');
    setSideAFilters(emptyFilterGroup());
    setSideAJoinDetails(false);
    setSideADetailFilters(emptyFilterGroup());
    setSideADataLag(0);
    setSideBAccountId('');
    setSideBMetricField('');
    setSideBMetricFunction('SUM');
    setSideBTimeAnchor('transactions.transaction_timestamp');
    setSideBGranularity('month');
    setSideBFilters(emptyFilterGroup());
    setSideBJoinDetails(false);
    setSideBDetailFilters(emptyFilterGroup());
    setSideBPeriodOffset(0);
    setSideBDataLag(0);
    setBondType('direct_proportional');
    setBondDirection('a_drives_b');
    setBondParams({});
  };

  const loadForEdit = (rel: RelationshipRecord) => {
    setEditingId(rel.id);
    setName(rel.name);
    setSideAAccountId(rel.side_a_account_id);
    setSideAMetricField(rel.definition.side_a.metric.field);
    setSideAMetricFunction(rel.definition.side_a.metric.function);
    setSideATimeAnchor(rel.definition.side_a.query.time_anchor);
    setSideAGranularity(rel.definition.side_a.aggregation.granularity);
    setSideAFilters(rel.definition.side_a.query.filters || emptyFilterGroup());
    setSideAJoinDetails(rel.definition.side_a.query.join_details || false);
    setSideADetailFilters(rel.definition.side_a.query.detail_filters || emptyFilterGroup());
    setSideADataLag(rel.definition.side_a.aggregation.data_lag?.value || 0);
    setSideBAccountId(rel.side_b_account_id);
    setSideBMetricField(rel.definition.side_b.metric.field);
    setSideBMetricFunction(rel.definition.side_b.metric.function);
    setSideBTimeAnchor(rel.definition.side_b.query.time_anchor);
    setSideBGranularity(rel.definition.side_b.aggregation.granularity);
    setSideBFilters(rel.definition.side_b.query.filters || emptyFilterGroup());
    setSideBJoinDetails(rel.definition.side_b.query.join_details || false);
    setSideBDetailFilters(rel.definition.side_b.query.detail_filters || emptyFilterGroup());
    setSideBPeriodOffset(rel.definition.side_b.aggregation.period_offset || 0);
    setSideBDataLag(rel.definition.side_b.aggregation.data_lag?.value || 0);
    setBondType(rel.definition.bond.bond_type);
    setBondDirection(rel.definition.bond.direction);
    // The backend's `parameters` is a loose blob; in the UI we only ever build
    // numeric fields here, so coerce after stripping non-numeric entries.
    const numericParams: Record<string, number | undefined> = {};
    for (const [k, v] of Object.entries(rel.definition.bond.parameters || {})) {
      if (typeof v === 'number') numericParams[k] = v;
    }
    setBondParams(numericParams);
    setShowBuilder(true);
  };

  const buildDefinition = (): RelationshipDefinition => ({
    side_a: {
      query: {
        filters: sideAFilters.conditions.length > 0 ? sideAFilters : undefined,
        join_details: sideAJoinDetails || undefined,
        detail_filters: sideAJoinDetails && sideADetailFilters.conditions.length > 0 ? sideADetailFilters : undefined,
        time_anchor: sideATimeAnchor,
      },
      metric: { field: sideAMetricField, function: sideAMetricFunction },
      aggregation: {
        granularity: sideAGranularity,
        data_lag: sideADataLag > 0 ? { value: sideADataLag, unit: 'days' } : undefined,
      },
    },
    side_b: {
      query: {
        filters: sideBFilters.conditions.length > 0 ? sideBFilters : undefined,
        join_details: sideBJoinDetails || undefined,
        detail_filters: sideBJoinDetails && sideBDetailFilters.conditions.length > 0 ? sideBDetailFilters : undefined,
        time_anchor: sideBTimeAnchor,
      },
      metric: { field: sideBMetricField, function: sideBMetricFunction },
      aggregation: {
        granularity: sideBGranularity,
        period_offset: sideBPeriodOffset,
        data_lag: sideBDataLag > 0 ? { value: sideBDataLag, unit: 'days' } : undefined,
      },
    },
    bond: {
      bond_type: bondType,
      direction: bondDirection,
      parameters: bondParams,
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!sideAAccountId || !sideBAccountId) {
      toast.error('Both accounts must be selected');
      return;
    }
    if (sideAAccountId === sideBAccountId) {
      toast.error('Side A and Side B must be different accounts');
      return;
    }

    const definition = buildDefinition();

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: { name, definition } });
    } else {
      createMutation.mutate({
        name,
        side_a_account_id: sideAAccountId,
        side_b_account_id: sideBAccountId,
        definition,
      });
    }
  };

  const handleValidate = (id: string) => {
    setValidatingId(id);
    validateMutation.mutate(id);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this relationship? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (accountsLoading || relationshipsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Cross-Account Relationships</h1>
          <p className="text-muted-foreground mt-1">
            Define and validate mathematical relationships between data source accounts.
          </p>
        </div>
        {!showBuilder && (
          <Button onClick={() => setShowBuilder(true)}>
            <Plus size={16} className="mr-2" />
            New Relationship
          </Button>
        )}
      </div>

      {/* Builder */}
      {showBuilder && (
        <Card>
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-foreground">
                {editingId ? 'Edit Relationship' : 'New Relationship'}
              </h2>
              <button onClick={resetBuilder} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>

            {/* Name */}
            <div>
              <label className="text-sm font-medium text-foreground">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Uber Eats Sales vs Commission Fees"
                className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
              />
            </div>

            {/* Sides */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SideConfig
                label="Side A (Driver)"
                accountId={sideAAccountId}
                onAccountChange={setSideAAccountId}
                accounts={accounts}
                metricField={sideAMetricField}
                onMetricFieldChange={setSideAMetricField}
                metricFunction={sideAMetricFunction}
                onMetricFunctionChange={setSideAMetricFunction}
                timeAnchor={sideATimeAnchor}
                onTimeAnchorChange={setSideATimeAnchor}
                granularity={sideAGranularity}
                onGranularityChange={setSideAGranularity}
                filters={sideAFilters}
                onFiltersChange={setSideAFilters}
                joinDetails={sideAJoinDetails}
                onJoinDetailsChange={setSideAJoinDetails}
                detailFilters={sideADetailFilters}
                onDetailFiltersChange={setSideADetailFilters}
                dataLag={sideADataLag}
                onDataLagChange={setSideADataLag}
                schema={sideASchema}
              />
              <SideConfig
                label="Side B (Effect)"
                accountId={sideBAccountId}
                onAccountChange={setSideBAccountId}
                accounts={accounts}
                metricField={sideBMetricField}
                onMetricFieldChange={setSideBMetricField}
                metricFunction={sideBMetricFunction}
                onMetricFunctionChange={setSideBMetricFunction}
                timeAnchor={sideBTimeAnchor}
                onTimeAnchorChange={setSideBTimeAnchor}
                granularity={sideBGranularity}
                onGranularityChange={setSideBGranularity}
                filters={sideBFilters}
                onFiltersChange={setSideBFilters}
                joinDetails={sideBJoinDetails}
                onJoinDetailsChange={setSideBJoinDetails}
                detailFilters={sideBDetailFilters}
                onDetailFiltersChange={setSideBDetailFilters}
                periodOffset={sideBPeriodOffset}
                onPeriodOffsetChange={setSideBPeriodOffset}
                dataLag={sideBDataLag}
                onDataLagChange={setSideBDataLag}
                schema={sideBSchema}
              />
            </div>

            {/* Bond Configuration */}
            <div className="p-4 border rounded-lg space-y-4">
              <h4 className="font-semibold text-foreground">Bond</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground">Type</label>
                  <select
                    value={bondType}
                    onChange={(e) => { setBondType(e.target.value); setBondParams({}); }}
                    className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                  >
                    {BOND_TYPES.map((bt) => (
                      <option key={bt.value} value={bt.value}>{bt.label} — {bt.description}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground">Direction</label>
                  <select
                    value={bondDirection}
                    onChange={(e) => setBondDirection(e.target.value)}
                    className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                  >
                    {DIRECTIONS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bond-type-specific parameters */}
              {bondType === 'direct_proportional' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">Expected Ratio</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bondParams.expected_ratio ?? ''}
                      onChange={(e) => setBondParams({ ...bondParams, expected_ratio: parseFloat(e.target.value) || undefined })}
                      placeholder="e.g., 0.30"
                      className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Tolerance %</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bondParams.tolerance_pct ?? ''}
                      onChange={(e) => setBondParams({ ...bondParams, tolerance_pct: parseFloat(e.target.value) || undefined })}
                      placeholder="e.g., 0.05"
                      className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                    />
                  </div>
                </div>
              )}

              {bondType === 'linear_with_offset' && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">Expected Slope</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bondParams.expected_slope ?? ''}
                      onChange={(e) => setBondParams({ ...bondParams, expected_slope: parseFloat(e.target.value) || undefined })}
                      className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Expected Intercept</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bondParams.expected_intercept ?? ''}
                      onChange={(e) => setBondParams({ ...bondParams, expected_intercept: parseFloat(e.target.value) || undefined })}
                      className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Tolerance %</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bondParams.tolerance_pct ?? ''}
                      onChange={(e) => setBondParams({ ...bondParams, tolerance_pct: parseFloat(e.target.value) || undefined })}
                      className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                    />
                  </div>
                </div>
              )}

              {bondType === 'ratio_bounded' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">Ratio Min</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bondParams.ratio_min ?? ''}
                      onChange={(e) => setBondParams({ ...bondParams, ratio_min: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g., 0.25"
                      className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Ratio Max</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bondParams.ratio_max ?? ''}
                      onChange={(e) => setBondParams({ ...bondParams, ratio_max: parseFloat(e.target.value) || 1 })}
                      placeholder="e.g., 0.35"
                      className="mt-1 w-full rounded border border-input px-3 py-2 text-sm text-foreground bg-card"
                    />
                  </div>
                </div>
              )}

              {bondType === 'correlation' && (
                <p className="text-sm text-muted-foreground">
                  Correlation has no required parameters. Run validation to compute Pearson r and Spearman rho.
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={resetBuilder}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {(createMutation.isPending || updateMutation.isPending) && <LoadingSpinner size="sm" />}
                {editingId ? 'Update' : 'Create'} Relationship
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Relationships List */}
      {relationships.length === 0 && !showBuilder ? (
        <Card>
          <div className="p-12 text-center">
            <GitBranch size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No relationships yet</h3>
            <p className="text-muted-foreground mb-4">
              Define mathematical relationships between your data source accounts to enable cross-account analysis.
            </p>
            <Button onClick={() => setShowBuilder(true)}>
              <Plus size={16} className="mr-2" />
              Create your first relationship
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {relationships.map((rel) => (
            <Card key={rel.id}>
              <div className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <GitBranch size={18} className="text-info flex-shrink-0" />
                    <h3 className="font-medium text-foreground truncate">{rel.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[rel.status] || STATUS_COLORS.draft}`}>
                      {rel.status}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {rel.side_a_account_name} &harr; {rel.side_b_account_name}
                    <span className="mx-2">|</span>
                    {BOND_TYPES.find((bt) => bt.value === rel.definition.bond.bond_type)?.label || rel.definition.bond.bond_type}
                    <span className="mx-2">|</span>
                    Created {new Date(rel.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleValidate(rel.id)}
                    disabled={validatingId === rel.id}
                  >
                    {validatingId === rel.id ? <LoadingSpinner size="sm" /> : <Play size={14} />}
                    <span className="ml-1">Validate</span>
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => loadForEdit(rel)}>
                    <Edit2 size={14} />
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => handleDelete(rel.id)}>
                    <Trash2 size={14} className="text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RelationshipsPage;
